import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dismissGroupNotifications, fetchDismissedGroupNotifications } from "shared/mobile/api/NotificationDismissals";
import type { AppNotification } from "types/Notification";

function readDismissedIds(storageKey: string): string[] {
    if (typeof window === "undefined") return [];
    try {
        const raw = window.localStorage.getItem(storageKey);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
    } catch {
        return [];
    }
}

function writeDismissedIds(storageKey: string, ids: string[]) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch {
        // Dismissal is a convenience. If storage is blocked, keep the UI usable.
    }
}

function mergeIdSets(a: Set<string>, b: Iterable<string>): Set<string> {
    const next = new Set(a);
    for (const id of b) next.add(id);
    return next;
}

/**
 * Tracks dismissed in-app guide notifications. When `authToken` is set, merges with
 * account-level dismissals from the server and POSTs new dismissals so they sync across devices.
 */
export function useDismissedNotifications(storageKey: string, authToken?: string | null) {
    const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set(readDismissedIds(storageKey)));
    const tokenRef = useRef(authToken);
    tokenRef.current = authToken;

    useEffect(() => {
        setDismissedIds(new Set(readDismissedIds(storageKey)));
    }, [storageKey]);

    useEffect(() => {
        const token = authToken?.trim();
        if (!token) return;

        let cancelled = false;
        void (async () => {
            try {
                const { ids } = await fetchDismissedGroupNotifications(token);
                if (cancelled || !Array.isArray(ids)) return;
                setDismissedIds((current) => {
                    const next = mergeIdSets(current, ids);
                    writeDismissedIds(storageKey, [...next]);
                    return next;
                });
            } catch {
                // keep local-only state
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authToken, storageKey]);

    const dismissNotification = useCallback((item: AppNotification) => {
        setDismissedIds((current) => {
            const next = mergeIdSets(current, [item.id]);
            writeDismissedIds(storageKey, [...next]);
            return next;
        });

        const token = tokenRef.current?.trim();
        if (token) {
            void dismissGroupNotifications(token, [item.id])
                .then((res) => {
                    if (res?.ids && Array.isArray(res.ids)) {
                        setDismissedIds((current) => {
                            const next = mergeIdSets(current, res.ids);
                            writeDismissedIds(storageKey, [...next]);
                            return next;
                        });
                    }
                })
                .catch(() => {
                    /* local state already updated */
                });
        }
    }, [storageKey]);

    const filteredIds = useMemo(() => dismissedIds, [dismissedIds]);

    const filterDismissedNotifications = useCallback(
        (items: AppNotification[]) => items.filter((item) => !filteredIds.has(item.id)),
        [filteredIds],
    );

    return {
        dismissedIds,
        dismissNotification,
        filterDismissedNotifications,
    };
}
